import React, { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Steps, Typography } from "antd";
import {
  CampusSummary,
  CreateCampusInput,
} from "../../../../services/schools/campus.types";
import {
  createCampus,
  updateCampus,
} from "../../../../services/schools/school.service";
import { SelectionCell } from "../../../../services/classrooms/classrooms.types";
import { getApiErrorMessage } from "../../../../utils";
import { useNotification } from "../../../../hooks/useNotification";
import SectionGradeProgramGrid from "../SchoolsTab/SectionGradeProgramGrid";
import { countGenerationDelta } from "../SchoolsTab/SectionGradeProgramGrid.util";
import GenerarClasesModal from "../ClassesTab/GenerarClasesModal";
import {
  buildCampusPayload,
  CampusDraft,
  emptyCampusDraft,
  validateCampusDraft,
} from "./CampusesTab.utils";

const { Text } = Typography;

interface CampusFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Escuela raíz del árbol: es el `:schoolId` de los endpoints de plantel. */
  rootSchoolId: number;
  /** Planteles ya existentes — la validación de abreviatura corre contra ellos. */
  campuses: CampusSummary[];
  /** Presente => modo edición. Ausente => alta con paso de clases. */
  campus?: CampusSummary | null;
  onSaved: () => void;
}

/**
 * Alta/edición de un plantel. El alta tiene dos pasos: "Datos" crea la fila y
 * "Clases a generar" reusa el MISMO grid + modal de generación de la escuela
 * principal, apuntados al id recién creado — `GenerateSelectionPayload` no
 * lleva school_id en el body (viaja en la URL), así que la generación funciona
 * contra un plantel sin tocar nada.
 *
 * Los grupos NO se piden en el paso 1: el grid los emite por grado y
 * `GenerarClasesModal` los persiste aditivamente con `addGroupToSchool`, igual
 * que en la escuela principal. Un control aparte los guardaría dos veces.
 */
const CampusFormModal: React.FC<CampusFormModalProps> = ({
  open,
  onClose,
  rootSchoolId,
  campuses,
  campus,
  onSaved,
}) => {
  const isEdit = Boolean(campus);
  const [draft, setDraft] = useState<CampusDraft>(emptyCampusDraft);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [createdCampusId, setCreatedCampusId] = useState<number | null>(null);
  const [generationSelections, setGenerationSelections] = useState<
    SelectionCell[]
  >([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const { notify } = useNotification();
  const [modal, modalContextHolder] = Modal.useModal();

  useEffect(() => {
    if (!open) return;
    setDraft(
      campus
        ? {
            name: campus.name,
            abbreviation: campus.abbreviation ?? "",
            cct: campus.cct ?? "",
          }
        : emptyCampusDraft
    );
    setError(null);
    setCreatedCampusId(null);
    setGenerationSelections([]);
    setGroups([]);
    setGenerateModalOpen(false);
  }, [open, campus]);

  const onClassesStep = createdCampusId !== null;
  const generationDelta = countGenerationDelta(generationSelections, []);

  const handleSubmit = async () => {
    const validationError = validateCampusDraft(
      draft,
      campuses,
      campus?.id
    );
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const payload = buildCampusPayload(draft) as CreateCampusInput;
      if (campus) {
        const response = await updateCampus(rootSchoolId, campus.id, payload);
        if (!response) {
          notify("error", "Error", "No se pudo actualizar el plantel.");
          return;
        }
        notify("success", "Éxito", "Plantel actualizado correctamente.");
        onSaved();
        onClose();
        return;
      }
      const response = await createCampus(rootSchoolId, payload);
      if (!response?.id) {
        notify("error", "Error", "No se pudo crear el plantel.");
        return;
      }
      notify("success", "Éxito", "Plantel creado correctamente.");
      setCreatedCampusId(response.id);
      onSaved();
    } catch (submitError) {
      notify(
        "error",
        "Error",
        getApiErrorMessage(submitError, "No se pudo guardar el plantel.")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRequestClose = () => {
    if (!onClassesStep) {
      onClose();
      return;
    }
    modal.confirm({
      title: "Salir sin generar clases",
      content:
        "El plantel ya quedó creado. Si salís ahora no va a tener clases; podés generarlas más tarde desde la tab Planteles.",
      okText: "Salir",
      cancelText: "Seguir acá",
      onOk: onClose,
    });
  };

  return (
    <>
      {modalContextHolder}
      <Modal
        open={open}
        title={isEdit ? "Editar plantel" : "Agregar plantel"}
        onCancel={handleRequestClose}
        width={onClassesStep ? 900 : 520}
        destroyOnClose
        footer={
          onClassesStep
            ? [
                <Button key="close" onClick={handleRequestClose}>
                  Cerrar
                </Button>,
                <Button
                  key="generate"
                  type="primary"
                  disabled={generationDelta.newCount === 0}
                  onClick={() => setGenerateModalOpen(true)}
                >
                  Generar clases
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={handleRequestClose}>
                  Cancelar
                </Button>,
                <Button
                  key="submit"
                  type="primary"
                  loading={saving}
                  onClick={handleSubmit}
                >
                  {isEdit ? "Guardar cambios" : "Crear plantel"}
                </Button>,
              ]
        }
      >
        {!isEdit && (
          <Steps
            size="small"
            current={onClassesStep ? 1 : 0}
            style={{ marginBottom: 24 }}
            items={[{ title: "Datos" }, { title: "Clases a generar" }]}
          />
        )}

        {onClassesStep ? (
          <SectionGradeProgramGrid
            key={createdCampusId ?? "new"}
            setGenerationSelections={setGenerationSelections}
            groups={groups}
            setGroups={setGroups}
          />
        ) : (
          <Form layout="vertical">
            <Form.Item
              label="Nombre"
              htmlFor="campus-name"
              validateStatus={error && !draft.name.trim() ? "error" : undefined}
            >
              <Input
                id="campus-name"
                aria-label="Nombre del plantel"
                placeholder="Ej. Octavio Paz - Anexo"
                value={draft.name}
                onChange={(event) =>
                  setDraft({ ...draft, name: event.target.value })
                }
              />
            </Form.Item>

            <Form.Item label="Abreviatura" htmlFor="campus-abbreviation">
              <Input
                id="campus-abbreviation"
                aria-label="Abreviatura del plantel"
                placeholder="Ej. OPA"
                value={draft.abbreviation}
                onChange={(event) =>
                  setDraft({ ...draft, abbreviation: event.target.value })
                }
              />
            </Form.Item>

            <Form.Item label="CCT" htmlFor="campus-cct">
              <Input
                id="campus-cct"
                aria-label="CCT del plantel"
                placeholder="Ingrese el CCT"
                value={draft.cct}
                onChange={(event) =>
                  setDraft({ ...draft, cct: event.target.value })
                }
              />
              <Text type="secondary">
                Dejalo vacío si el plantel no tiene CCT propio.
              </Text>
            </Form.Item>

            {error && (
              <div role="alert" style={{ color: "#ff4d4f" }}>
                {error}
              </div>
            )}
          </Form>
        )}
      </Modal>

      {createdCampusId !== null && (
        <GenerarClasesModal
          open={generateModalOpen}
          onClose={() => setGenerateModalOpen(false)}
          schoolId={createdCampusId}
          selections={generationSelections}
          groups={groups}
          existingGroups={[]}
          refreshClasses={async () => {}}
          onGenerated={() => {
            onSaved();
            onClose();
          }}
        />
      )}
    </>
  );
};

export default CampusFormModal;
